import type {
  VitalSignRecord,
  VitalSignRecordInput,
} from "./types";
import { buildConsultationVitals, normalizeVitalSignRecord } from "./types";
import { readJson, writeJson } from "@/core/tenant/hospitol-storage";
import {
  apiListVitalSignRecords,
  apiSyncVitalSignRecord,
  apiValidateVitalSignRecord,
} from "@/core/hospitol-api-client";

const RECORDS_KEY = "hospitol.nursing.vitalSigns";

function readRecords(): VitalSignRecord[] {
  return readJson<Record<string, unknown>[]>(RECORDS_KEY, [])
    .map(normalizeVitalSignRecord)
    .filter((r): r is VitalSignRecord => r != null);
}

function writeRecords(records: VitalSignRecord[]): void {
  writeJson(RECORDS_KEY, records);
}

function sortByRecordedDesc(a: VitalSignRecord, b: VitalSignRecord): number {
  return new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime();
}

export class VitalSignValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "VitalSignValidationError";
  }
}

/** Pull vitals from API (WP-HOSP3); push local if remote empty. */
export async function syncVitalSignRecordsFromApi(): Promise<boolean> {
  const remote = await apiListVitalSignRecords();
  if (remote === null) return false;
  if (remote.length > 0) {
    writeRecords(
      remote
        .map((r) => normalizeVitalSignRecord(r as unknown as Record<string, unknown>))
        .filter((r): r is VitalSignRecord => r != null),
    );
    return true;
  }
  for (const record of readRecords()) void apiSyncVitalSignRecord(record);
  return true;
}

export function listVitalSignRecords(): VitalSignRecord[] {
  return readRecords().sort(sortByRecordedDesc);
}

export function listVitalSignRecordsByPatient(patientId: string): VitalSignRecord[] {
  return listVitalSignRecords()
    .filter((r) => r.patientId === patientId)
    .sort((a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime());
}

export function listVitalSignRecordsByAdmission(admissionId: string): VitalSignRecord[] {
  return listVitalSignRecords()
    .filter((r) => r.admissionId === admissionId)
    .sort((a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime());
}

export function getLatestVitalSignForPatient(patientId: string): VitalSignRecord | null {
  return listVitalSignRecordsByPatient(patientId)[0] ?? null;
}

/** Relevés infirmiers récents pour import en consultation (7 derniers jours). */
export function listRecentNursingVitalsForPatient(
  patientId: string,
  maxAgeDays = 7,
): VitalSignRecord[] {
  const cutoff = Date.now() - maxAgeDays * 24 * 60 * 60 * 1000;
  return listVitalSignRecordsByPatient(patientId).filter(
    (r) => new Date(r.recordedAt).getTime() >= cutoff,
  );
}

export function getVitalSignRecordById(id: string): VitalSignRecord | null {
  return listVitalSignRecords().find((r) => r.id === id) ?? null;
}

export function createVitalSignRecord(input: VitalSignRecordInput): VitalSignRecord {
  const vitals = buildConsultationVitals(input.vitals);
  if (!vitals) {
    throw new VitalSignValidationError("Saisissez au moins une constante.");
  }

  const now = new Date().toISOString();
  const record: VitalSignRecord = {
    id: `vsr-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    patientId: input.patientId,
    recordedById: input.recordedById,
    recordedByName: input.recordedByName.trim(),
    context: input.context,
    admissionId: input.admissionId ?? null,
    consultationId: input.consultationId ?? null,
    stayId: input.stayId ?? null,
    vitals,
    status: "recorded",
    recordedAt: now,
    validatedAt: null,
    notes: input.notes?.trim() ?? "",
  };

  const list = readRecords();
  list.unshift(record);
  writeRecords(list);
  void apiSyncVitalSignRecord(record);
  void import("@/core/billing/extended-hospital-billing").then(({ autoBillNursingOnVitals }) =>
    autoBillNursingOnVitals(record),
  );
  return record;
}

export function validateVitalSignRecord(id: string): VitalSignRecord | null {
  const list = readRecords();
  const index = list.findIndex((r) => r.id === id);
  if (index < 0) return null;
  const now = new Date().toISOString();
  list[index] = {
    ...list[index]!,
    status: "validated",
    validatedAt: now,
  };
  writeRecords(list);
  const validated = list[index]!;
  void apiValidateVitalSignRecord(id).then((remote) => {
    if (!remote) return;
    const next = readRecords();
    const idx = next.findIndex((r) => r.id === id);
    const normalized = normalizeVitalSignRecord(remote as unknown as Record<string, unknown>);
    if (idx >= 0 && normalized) {
      next[idx] = normalized;
      writeRecords(next);
    }
  });
  return validated;
}

export function countVitalSignsToday(): number {
  const today = new Date().toDateString();
  return listVitalSignRecords().filter(
    (r) => new Date(r.recordedAt).toDateString() === today,
  ).length;
}
